import { EventEmitter } from '@angular/core';
import * as _ from 'lodash';
import { DisplayDecorator, DisplayDecoratorFromJson, JsonDisplayDecorator } from '../../decorators/display-decorator';
import { ODataModel, ODataObject } from '../../interfaces/odata-model.interface';
import {
    DropdownModelOptions,
    DropdownModelOptionsFromJson,
    JsonDropdownModelOptions,
} from '../options/dropdown-model-options';
import { ArrayModel } from './array.model';

export class DropdownModel<T extends ODataModel> extends ArrayModel<DisplayDecorator<T>> {
    public selectedEntries: Array<DisplayDecorator<T>> = [];
    public selectionChanged = new EventEmitter<Array<DisplayDecorator<T>>>();
    public previousSelectedEntries: Array<DisplayDecorator<T>> = [];

    // this is a shorthand to access to first of the selected entries; especially useful if maxSelections == 1 as you
    // don't need to write the array access every time you want to get the one selected entry.
    public get selectedEntry(): DisplayDecorator<T> {
        return this.selectedEntries[0];
    }
    public constructor(
        name: string,
        entries: Array<DisplayDecorator<T>>,
        public options = new DropdownModelOptions(),
        public textFieldValue = '',
    ) {
        super(name, entries, options);
    }

    /**
     * Iterate over internal models array and call outmodel conversion function for each of the models
     *
     * @returns {object} the outmodel representation of this array model
     */
    public getOutModel(): ODataDropdownOutModel {
        const outModel: ODataDropdownOutModel = {};
        outModel[this.name] = '';

        if (this.options.displayText) {
            outModel[this.name] = this.textFieldValue;
            return outModel;
        }
        if (this.options.maxSelections === 1 && this.selectedEntries.length === 1) {
            const selectedOutModel = this.selectedEntry.getOutModel();
            outModel[this.name] = selectedOutModel[this.selectedEntry.name];
        } else if (this.options.maxSelections > 1) {
            outModel[this.name] = this.selectedEntries.map((selectedEntry) => {
                const selectedOutModel = selectedEntry.getOutModel();
                return selectedOutModel[selectedEntry.name];
            });
        }

        return outModel;
    }

    /**
     * Select an entry in the dropdown
     *
     * @param index
     * @returns {boolean} true if the selection could be done, false otherwise
     */
    public selectByIndex(index: number): boolean {
        if (index < 0 || index >= this.value.length) {
            return false;
        }

        const selectedEntry = this.value[index];
        let hasSelectionChanged = false;

        // if only one entry can be selected
        if (this.options.maxSelections === 1) {
            // add new entry if it is different than the existing entry
            if (this.selectedEntry !== selectedEntry) {
                this.selectedEntries.push(selectedEntry);
                hasSelectionChanged = true;
            }

            if (this.selectedEntries.length > 1) {
                // remove formerly selected entry
                this.selectedEntries.shift();
                hasSelectionChanged = true;
            }
        } else {
            // if multiple entries can be selected
            // De-select entry if previously selected and is not part of alwaysSelectedEntries, else select it
            if (
                this.selectedEntries.includes(selectedEntry) &&
                !this.options.alwaysSelectedEntries.includes(`${selectedEntry.value}`)
            ) {
                this.selectedEntries = this.selectedEntries.filter((e) => e !== selectedEntry);
                hasSelectionChanged = true;
            } else if (!this.selectedEntries.includes(selectedEntry)) {
                // check if we can select an additional entry
                if (this.selectedEntries.length >= this.options.maxSelections) {
                    return false;
                }
                this.selectedEntries.push(this.value[index]);
                hasSelectionChanged = true;
            }
        }

        if (hasSelectionChanged) {
            this.selectionChanged.emit(this.selectedEntries);
        }
        return hasSelectionChanged;
    }

    public selectByDisplayName(displayName: string): boolean {
        const selectedIndex = this.value.findIndex((value) => value.displayName === displayName);

        return this.selectByIndex(selectedIndex);
    }

    /**
     * This is a convenience function that might be considerably slower than selection by predicate because it does
     * a deep comparison of all properties in the model.
     *
     * Please don't use it in loops or similar. If there are ever performance problems with this, you could instead use
     * selectByPredicate(..) where you can simply compare based on a selected property instead of all properties.
     *
     * @param {T} entry
     * @returns {boolean}
     */
    public selectByEntry(entry: T): boolean {
        const selectedIndex = this.value.findIndex((e) => _.isEqual(e.model, entry));

        return this.selectByIndex(selectedIndex);
    }

    public selectByValue(value: unknown): boolean {
        const selectedIndex = this.value.findIndex((e) => e.value === value);

        return this.selectByIndex(selectedIndex);
    }
    public selectByPredicate(predicate: (entry: DisplayDecorator<T>) => boolean): boolean {
        const index = this.value.findIndex(predicate);

        return this.selectByIndex(index);
    }

    public deselect(): void {
        this.textFieldValue = '';
        this.previousSelectedEntries = _.cloneDeep(this.selectedEntries);
        this.selectedEntries.length = 0;
        this.selectionChanged.emit(this.selectedEntries);
    }

    public restore(): void {
        this.previousSelectedEntries.forEach((entry) => {
            const selectedEntryIndex = this.value.findIndex((valueEntry) => valueEntry.value === entry.value);
            this.selectByIndex(selectedEntryIndex);
        });
        this.previousSelectedEntries.length = 0;
    }
}

export class DropdownModelFromJson<T extends ODataModel> extends DropdownModel<T> {
    public constructor(type: new (...args: unknown[]) => T, data: JsonDropdownModel) {
        super(
            data.name,
            data.value.map((e) => {
                return new DisplayDecoratorFromJson(type, e);
            }),
            new DropdownModelOptionsFromJson(data.options),
            data.textFieldValue,
        );

        if (data.options.displayText) {
            this.textFieldValue = data.textFieldValue;
            return;
        }
        // restore selections
        if (data.selectedEntries.length > 0) {
            data.selectedEntries.forEach((entry: JsonDisplayDecorator) => {
                const selectedEntryIndex = data.value.findIndex((e) => e === entry);
                this.selectByIndex(selectedEntryIndex);
            });
        }
        this.selectedEntries = data.selectedEntries.map((e) => {
            return new DisplayDecoratorFromJson(type, e);
        });
        this.previousSelectedEntries = data.previousSelectedEntries.map((e) => {
            return new DisplayDecoratorFromJson(type, e);
        });
    }
}

export interface JsonDropdownModel {
    selectedEntries: JsonDisplayDecorator[];
    previousSelectedEntries: JsonDisplayDecorator[];
    name: string;
    value: JsonDisplayDecorator[];
    options: JsonDropdownModelOptions;
    textFieldValue: string;
}

// need here type because empty interfaces are not allowed
export type ODataDropdownOutModel = ODataObject;
